import { ExplicitExpectationEditor } from "src/pages/DataAssets/views/Expectations/SimpleExpectationDrawer/ExplicitExpectationEditor.tsx"
import { ConfigProvider, FormInstance, Segmented } from "antd"
import { DataQualityIssue } from "src/global/schemas/expectations/expectation-metadata-data-quality-issues.ts"
import { SelectedExpectation } from "src/pages/DataAssets/views/Expectations/Expectation/CreateExpectationDrawer/types.ts"
import {
  AnomalyDetectionCoverageStats,
  ExpectationEditor_DataAssetMetricRunFragmentDoc,
} from "src/api/graphql/graphql.ts"
import { ApolloError } from "@apollo/client"
import { useIsFeatureEnabled } from "src/global/hooks/useIsFeatureEnabled.ts"
import { useCallback } from "react"
import { AnomalyDetectionExpectationEditor } from "src/pages/DataAssets/views/Expectations/SimpleExpectationDrawer/AnomalyDetectionExpectationEditor.tsx"
import { theme } from "src/global/ui/themes/theme.ts"
import { styled } from "styled-components"
import { Image } from "src/global/ui/Image"
import { Icon } from "src/global/ui/Icon"
import { FragmentType } from "src/api/graphql"

const StyledResultContainer = styled.div`
  padding-top: ${theme.spacing.s};
  padding-bottom: ${theme.spacing.m};
`

const StyledIconContainer = styled.span`
  svg {
    width: ${theme.spacing.xs};
    height: ${theme.spacing.xs};
    padding-top: ${theme.spacing.xxs};
  }
  .ant-segmented-item-selected & svg path {
    fill: ${theme.colors.neutralColorPalette.whites.white};
  }
`

const StyledSparklesIcon = () => {
  return (
    <StyledIconContainer>
      <Image type="sparkles" aria-label="Anomaly Detection" />
    </StyledIconContainer>
  )
}

const StyledSlidersIcon = () => {
  return (
    <StyledIconContainer>
      <Icon name="sliders" aria-label="Explicit" />
    </StyledIconContainer>
  )
}

export const expectationDrawerEditorOptions = [
  {
    value: "anomaly_detection",
    label: "Anomaly Detection",
    icon: <StyledSparklesIcon />,
  },
  {
    value: "explicit",
    label: "Explicit",
    icon: <StyledSlidersIcon />,
  },
]

type Props = {
  form: FormInstance
  selectedDataQualityIssue: DataQualityIssue | undefined
  onSelectExpectation: (expectation: SelectedExpectation) => void
  selectedExpectation: SelectedExpectation
  expectationConfiguration: Record<string, unknown>
  handleConfigChange: (newConfig: Record<string, unknown>) => void
  dataAsset: FragmentType<typeof ExpectationEditor_DataAssetMetricRunFragmentDoc> | undefined
  addExpectationToAssetError: ApolloError | undefined
  addAnomalyDetectionExpectationToAssetError?: ApolloError
  anomalyDetectionCoverageStats?: AnomalyDetectionCoverageStats | null
  selectedSegment: string
  setSelectedSegment: (segment: string) => void
}

export function ExpectationDrawerEditor({
  form,
  selectedDataQualityIssue,
  onSelectExpectation,
  selectedExpectation,
  expectationConfiguration,
  handleConfigChange,
  dataAsset,
  addExpectationToAssetError,
  addAnomalyDetectionExpectationToAssetError,
  anomalyDetectionCoverageStats,
  selectedSegment,
  setSelectedSegment,
}: Props) {
  const anomalyDetectionForExistingAssetsEnabled = useIsFeatureEnabled("anomalyDetectionForExistingAssetsEnabled")
  const dataQualityIssuesWithAnomalyDetection = ["Volume", "Schema"]
  const isAnomalyDetectionEnabledForDataQualityIssue = (dataQualityIssue: DataQualityIssue | undefined) => {
    return dataQualityIssue ? dataQualityIssuesWithAnomalyDetection.includes(dataQualityIssue) : false
  }

  const segmentContents = useCallback(() => {
    if (selectedSegment === "anomaly_detection" && selectedDataQualityIssue && anomalyDetectionCoverageStats) {
      return (
        <AnomalyDetectionExpectationEditor
          issueType={selectedDataQualityIssue}
          anomalyDetectionEditorCoverageStats={anomalyDetectionCoverageStats}
          addAnomalyDetectionExpectationToAssetError={addAnomalyDetectionExpectationToAssetError}
        />
      )
    }
    return (
      <>
        <StyledResultContainer>
          <div>Set your own rules for what &#34;bad data&#34; looks like:</div>
        </StyledResultContainer>
        <ExplicitExpectationEditor
          form={form}
          selectedDataQualityIssue={selectedDataQualityIssue}
          onSelectExpectation={onSelectExpectation}
          selectedExpectation={selectedExpectation}
          expectationConfiguration={expectationConfiguration}
          handleConfigChange={handleConfigChange}
          dataAsset={dataAsset}
          addExpectationToAssetError={addExpectationToAssetError}
        />
      </>
    )
  }, [
    addExpectationToAssetError,
    addAnomalyDetectionExpectationToAssetError,
    anomalyDetectionCoverageStats,
    dataAsset,
    expectationConfiguration,
    form,
    handleConfigChange,
    onSelectExpectation,
    selectedDataQualityIssue,
    selectedExpectation,
    selectedSegment,
  ])

  return (
    <ConfigProvider
      theme={{
        components: {
          Segmented: {
            itemSelectedBg: theme.colors.neutralColorPalette.blacks.colorText,
            itemSelectedColor: theme.colors.neutralColorPalette.whites.white,
            itemColor: theme.colors.neutralColorPalette.blacks.colorText,
            controlItemBgActiveDisabled: theme.colors.neutralColorPalette.blacks.colorTextQuaternary,
            fontSize: parseInt(theme.typography.fontSize.medium),
            controlHeight: 40,
          },
        },
      }}
    >
      {anomalyDetectionForExistingAssetsEnabled &&
      isAnomalyDetectionEnabledForDataQualityIssue(selectedDataQualityIssue) ? (
        <>
          <Segmented
            options={expectationDrawerEditorOptions}
            defaultValue={selectedSegment}
            onChange={(e) => setSelectedSegment(e)}
            data-testid="anomaly-detection-segmented"
          />
          {segmentContents()}
        </>
      ) : (
        <ExplicitExpectationEditor
          form={form}
          selectedDataQualityIssue={selectedDataQualityIssue}
          onSelectExpectation={onSelectExpectation}
          selectedExpectation={selectedExpectation}
          expectationConfiguration={expectationConfiguration}
          handleConfigChange={handleConfigChange}
          dataAsset={dataAsset}
          addExpectationToAssetError={addExpectationToAssetError}
        />
      )}
    </ConfigProvider>
  )
}
